import type { AppNotification } from "../types/notifications";

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isAppNotification(value: unknown): value is AppNotification {
  if (!isRecord(value)) return false;
  return typeof value._id === "string" && typeof value.templateKey === "string";
}

/**
 * `notification:new` has been emitted as both:
 * - `{ recipientUserId, notification }` (Redis socket bridge)
 * - the notification object itself (docs / older emitters)
 */
export function extractNotificationFromSocketPayload(
  data: unknown
): AppNotification | null {
  if (isAppNotification(data)) {
    return data;
  }
  if (isRecord(data) && isAppNotification(data.notification)) {
    return data.notification;
  }
  return null;
}

export function isTaskAssignmentNotification(
  notification: AppNotification
): boolean {
  return (
    notification.entityType === "TASK" ||
    notification.templateKey === "TASK_ASSIGNED" ||
    notification.eventType === "UnitPocQueryCreated" ||
    notification.eventType === "UnitPocQueryTransferred" ||
    notification.eventType === "TaskAssigned"
  );
}

/** CAR-2644: WorkOrderDue means the lead is pinned — refresh My Leads even if the priority socket is missed. */
export function isWorkOrderDueNotification(
  notification: Pick<AppNotification, "eventType">
): boolean {
  return notification.eventType === "WorkOrderDue";
}

/** CAR-2738: export completion/failure from the in-app notification pipeline. */
export function isExportCompletionNotification(
  notification: Pick<
    AppNotification,
    "eventType" | "_id" | "templateKey" | "entityType"
  >
): boolean {
  return (
    notification.eventType === "ExportCompleted" ||
    notification.eventType === "ExportFailed" ||
    notification.templateKey === "EXPORT_COMPLETED" ||
    notification.templateKey === "EXPORT_FAILED" ||
    notification.entityType === "EXPORT" ||
    notification._id.startsWith("local-export-")
  );
}
